import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import './TrailPrepScreen.css';

interface TrainingWeek {
  week: number;
  focus: string;
  activities: string[];
  duration: string;
  intensity: string;
  notes: string[];
}

interface FamousHike {
  id: string;
  name: string;
  location: string;
  difficulty: 'Easy' | 'Moderate' | 'Challenging' | 'Extreme';
  duration: string;
  distance: string;
  elevation: string;
  bestSeason: string;
  permits: string;
  preparation: {
    fitness: string[];
    gear: string[];
    permits: string[];
    logistics: string[];
    safety: string[];
    training: string[];
  };
  highlights: string[];
  challenges: string[];
  trainingPlan: TrainingWeek[];
}

const famousHikes: FamousHike[] = [
  {
    id: 'lake-tahoe-rim',
    name: 'Lake Tahoe Rim Trail',
    location: 'California/Nevada, USA',
    difficulty: 'Moderate',
    duration: '10-15 days',
    distance: '165 miles (265 km)',
    elevation: '6,000-10,000 ft (1,800-3,000 m)',
    bestSeason: 'June - October',
    permits: 'Desolation Wilderness permits required for overnight stays',
    preparation: {
      fitness: [
        'Build endurance with 8-12 mile training hikes',
        'Practice hiking with 30-40 lb pack',
        'Train on varied terrain including steep ascents',
        'Complete 15-20 mile day hikes before attempting',
        'Focus on leg strength and cardiovascular fitness'
      ],
      gear: [
        'Lightweight backpacking tent and sleeping system',
        'Water filtration system (multiple sources available)',
        'Bear canister for food storage',
        'Layered clothing for temperature changes',
        'Quality hiking boots with ankle support',
        'Trekking poles for stability and joint protection',
        'Comprehensive first aid kit',
        'Emergency communication device'
      ],
      permits: [
        'Desolation Wilderness permits required for overnight stays',
        'Apply for permits up to 6 months in advance',
        'Day hiking does not require permits',
        'Some areas may require bear canisters',
        'Check individual campground requirements',
        'Respect private property boundaries'
      ],
      logistics: [
        'Plan resupply points (Tahoe City, South Lake Tahoe)',
        'Arrange transportation to trailhead',
        'Book accommodations for rest days if needed',
        'Plan for weather changes and potential storms',
        'Consider shuttle services for transportation'
      ],
      safety: [
        'Carry the Ten Essentials at all times',
        'Be prepared for sudden weather changes',
        'Know how to use bear spray and store food properly',
        'Inform someone of your itinerary',
        'Check trail conditions before starting',
        'Be aware of altitude effects'
      ],
      training: [
        'Start training 3-4 months in advance',
        'Gradually increase pack weight and distance',
        'Practice navigation with map and compass',
        'Train in similar terrain and elevation',
        'Test all gear before the trip'
      ]
    },
    highlights: [
      'Stunning views of Lake Tahoe from multiple angles',
      'Diverse ecosystems from alpine to forest',
      'Multiple access points for resupply',
      'Well-maintained trail system',
      'Opportunities for side trips to peaks'
    ],
    challenges: [
      'High altitude can affect some hikers',
      'Weather can change quickly',
      'Some sections have limited water sources',
      'Popular trail can be crowded in peak season',
      'Steep ascents and descents'
    ],
    trainingPlan: [
      {
        week: 1,
        focus: 'Base Building',
        activities: ['3-4 mile hikes', 'Daily walking 30-45 minutes', 'Basic strength training'],
        duration: '4-6 hours/week',
        intensity: 'Easy to Moderate',
        notes: ['Start with flat terrain', 'Focus on consistency', 'Build daily movement habit']
      },
      {
        week: 2,
        focus: 'Endurance Foundation',
        activities: ['5-6 mile hikes', 'Daily walking 45-60 minutes', 'Leg strength exercises'],
        duration: '6-8 hours/week',
        intensity: 'Moderate',
        notes: ['Add slight elevation gain', 'Include squats and lunges', 'Practice with day pack']
      },
      {
        week: 3,
        focus: 'Distance Building',
        activities: ['7-8 mile hikes', 'Daily walking 60 minutes', 'Core and leg strengthening'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Increase pack weight to 15-20 lbs', 'Add hills and varied terrain', 'Focus on hiking form']
      },
      {
        week: 4,
        focus: 'Elevation Training',
        activities: ['8-10 mile hikes', 'Daily walking 60-75 minutes', 'Full body strength training'],
        duration: '10-12 hours/week',
        intensity: 'Moderate to Hard',
        notes: ['Seek out steep hills and stairs', 'Increase pack weight to 20-25 lbs', 'Practice uphill technique']
      },
      {
        week: 5,
        focus: 'Back-to-Back Training',
        activities: ['10-12 mile hikes', 'Consecutive day hiking', 'Endurance and strength'],
        duration: '12-15 hours/week',
        intensity: 'Hard',
        notes: ['Hike 2-3 consecutive days', 'Simulate trail conditions', 'Test gear and nutrition']
      },
      {
        week: 6,
        focus: 'Peak Training',
        activities: ['12-15 mile hikes', 'Long day hikes with elevation', 'Full pack training'],
        duration: '15-18 hours/week',
        intensity: 'Hard',
        notes: ['Pack weight 25-30 lbs', 'Include 2000+ ft elevation gain', 'Practice navigation skills']
      },
      {
        week: 7,
        focus: 'Taper and Recovery',
        activities: ['8-10 mile hikes', 'Light strength training', 'Active recovery'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Reduce intensity', 'Focus on recovery', 'Final gear checks']
      },
      {
        week: 8,
        focus: 'Final Preparation',
        activities: ['5-6 mile hikes', 'Light walking', 'Mental preparation'],
        duration: '4-6 hours/week',
        intensity: 'Easy',
        notes: ['Rest and recover', 'Final gear preparation', 'Plan logistics and permits']
      }
    ]
  },
  {
    id: 'john-muir-trail',
    name: 'John Muir Trail',
    location: 'California, USA',
    difficulty: 'Challenging',
    duration: '3-4 weeks',
    distance: '211 miles (340 km)',
    elevation: '4,000-14,500 ft (1,200-4,400 m)',
    bestSeason: 'July - September',
    permits: 'Wilderness permits required',
    preparation: {
      fitness: [
        'Exceptional cardiovascular fitness required',
        'Practice hiking with 40-50 lb pack',
        'Train at high altitude if possible',
        'Complete multiple 20+ mile training hikes',
        'Build leg strength for steep ascents and descents'
      ],
      gear: [
        'Ultralight backpacking gear (every ounce counts)',
        'Bear canister (required in many areas)',
        'High-quality sleeping bag rated for cold temperatures',
        'Water treatment system (multiple sources)',
        'Layered clothing system for extreme temperature changes',
        'Quality hiking boots with excellent traction',
        'Trekking poles for stability and joint protection',
        'Comprehensive first aid kit and emergency supplies'
      ],
      permits: [
        'Wilderness permits required for overnight stays',
        'Apply for permits 6 months in advance',
        'Popular trailheads have limited permits',
        'Consider alternative entry points',
        'Understand permit regulations and restrictions'
      ],
      logistics: [
        'Plan resupply strategy (Muir Trail Ranch, VVR)',
        'Arrange transportation to Yosemite and Whitney',
        'Book accommodations for rest days',
        'Plan for weather and potential early snow',
        'Consider hiring pack animals for resupply'
      ],
      safety: [
        'Carry comprehensive emergency supplies',
        'Be prepared for extreme weather changes',
        'Know how to handle altitude sickness',
        'Understand bear safety and food storage',
        'Have emergency communication device',
        'Be prepared for potential early season snow'
      ],
      training: [
        'Start training 6 months in advance',
        'Train at high altitude if possible',
        'Practice with full pack weight',
        'Complete multiple long-distance training hikes',
        'Test all gear in similar conditions'
      ]
    },
    highlights: [
      'Stunning High Sierra scenery',
      'Summit of Mount Whitney (highest peak in continental US)',
      'World-class alpine lakes and peaks',
      'Well-maintained trail with excellent signage',
      'Opportunity to see diverse wildlife'
    ],
    challenges: [
      'Extreme altitude changes',
      'Unpredictable weather and early snow',
      'Limited resupply options',
      'High permit competition',
      'Physically demanding terrain'
    ],
    trainingPlan: [
      {
        week: 1,
        focus: 'Base Building',
        activities: ['4-5 mile hikes', 'Daily walking 45-60 minutes', 'Basic strength training'],
        duration: '6-8 hours/week',
        intensity: 'Easy to Moderate',
        notes: ['Start with flat terrain', 'Focus on consistency', 'Build daily movement habit']
      },
      {
        week: 2,
        focus: 'Endurance Foundation',
        activities: ['6-8 mile hikes', 'Daily walking 60-75 minutes', 'Leg strength exercises'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Add elevation gain', 'Include squats and lunges', 'Practice with 20-25 lb pack']
      },
      {
        week: 3,
        focus: 'Distance Building',
        activities: ['8-12 mile hikes', 'Daily walking 75 minutes', 'Core and leg strengthening'],
        duration: '10-12 hours/week',
        intensity: 'Moderate',
        notes: ['Increase pack weight to 25-30 lbs', 'Add hills and varied terrain', 'Focus on hiking form']
      },
      {
        week: 4,
        focus: 'Elevation Training',
        activities: ['10-15 mile hikes', 'Daily walking 75-90 minutes', 'Full body strength training'],
        duration: '12-15 hours/week',
        intensity: 'Moderate to Hard',
        notes: ['Seek out steep hills and stairs', 'Increase pack weight to 30-35 lbs', 'Practice uphill technique']
      },
      {
        week: 5,
        focus: 'Back-to-Back Training',
        activities: ['12-18 mile hikes', 'Consecutive day hiking', 'Endurance and strength'],
        duration: '15-18 hours/week',
        intensity: 'Hard',
        notes: ['Hike 2-3 consecutive days', 'Simulate trail conditions', 'Test gear and nutrition']
      },
      {
        week: 6,
        focus: 'Peak Training',
        activities: ['15-20 mile hikes', 'Long day hikes with elevation', 'Full pack training'],
        duration: '18-20 hours/week',
        intensity: 'Hard',
        notes: ['Pack weight 35-40 lbs', 'Include 3000+ ft elevation gain', 'Practice navigation skills']
      },
      {
        week: 7,
        focus: 'Taper and Recovery',
        activities: ['10-12 mile hikes', 'Light strength training', 'Active recovery'],
        duration: '10-12 hours/week',
        intensity: 'Moderate',
        notes: ['Reduce intensity', 'Focus on recovery', 'Final gear checks']
      },
      {
        week: 8,
        focus: 'Final Preparation',
        activities: ['6-8 mile hikes', 'Light walking', 'Mental preparation'],
        duration: '6-8 hours/week',
        intensity: 'Easy',
        notes: ['Rest and recover', 'Final gear preparation', 'Plan logistics and permits']
      }
    ]
  },
  {
    id: 'w-trek',
    name: 'W Trek',
    location: 'Torres del Paine, Chile',
    difficulty: 'Moderate',
    duration: '4-5 days',
    distance: '50 miles (80 km)',
    elevation: 'Sea level to 2,500 ft (760 m)',
    bestSeason: 'October - April (Southern Hemisphere summer)',
    permits: 'Park entrance fee and camping reservations required',
    preparation: {
      fitness: [
        'Good cardiovascular fitness required',
        'Practice hiking with 25-35 lb pack',
        'Train for steep ascents and descents',
        'Build endurance for consecutive hiking days',
        'Focus on leg strength and stability'
      ],
      gear: [
        'Quality waterproof gear (Patagonia weather)',
        'Layered clothing for variable weather',
        'Sturdy hiking boots with good traction',
        'Trekking poles for stability',
        'Waterproof pack covers and dry bags',
        'Comprehensive first aid kit',
        'Emergency shelter and communication device'
      ],
      permits: [
        'Park entrance fee required',
        'Camping reservations needed in advance',
        'Limited camping spots available',
        'Book accommodations 6+ months ahead',
        'Consider refugio (mountain huts) options'
      ],
      logistics: [
        'Fly to Punta Arenas, then bus to Puerto Natales',
        'Arrange transportation to park entrance',
        'Book accommodations in Puerto Natales',
        'Plan for weather delays and changes',
        'Consider guided vs. self-guided options'
      ],
      safety: [
        'Be prepared for extreme weather changes',
        'Carry emergency supplies and communication device',
        'Understand wind safety (can reach 100+ mph)',
        'Know how to handle cold and wet conditions',
        'Inform someone of your itinerary',
        'Be prepared for potential trail closures'
      ],
      training: [
        'Start training 3-4 months in advance',
        'Practice hiking in windy conditions',
        'Train with full pack weight',
        'Complete multiple consecutive day hikes',
        'Test gear in wet and windy conditions'
      ]
    },
    highlights: [
      'Iconic Torres del Paine towers',
      'Stunning glacial lakes and icebergs',
      'Diverse Patagonian landscapes',
      'Opportunity to see guanacos and condors',
      'Well-maintained trail system'
    ],
    challenges: [
      'Extreme and unpredictable weather',
      'Very strong winds (can reach 100+ mph)',
      'Limited camping availability',
      'High cost of accommodations',
      'Remote location and logistics'
    ],
    trainingPlan: [
      {
        week: 1,
        focus: 'Base Building',
        activities: ['3-4 mile hikes', 'Daily walking 30-45 minutes', 'Basic strength training'],
        duration: '4-6 hours/week',
        intensity: 'Easy to Moderate',
        notes: ['Start with flat terrain', 'Focus on consistency', 'Build daily movement habit']
      },
      {
        week: 2,
        focus: 'Endurance Foundation',
        activities: ['5-6 mile hikes', 'Daily walking 45-60 minutes', 'Leg strength exercises'],
        duration: '6-8 hours/week',
        intensity: 'Moderate',
        notes: ['Add slight elevation gain', 'Include squats and lunges', 'Practice with day pack']
      },
      {
        week: 3,
        focus: 'Distance Building',
        activities: ['7-8 mile hikes', 'Daily walking 60 minutes', 'Core and leg strengthening'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Increase pack weight to 15-20 lbs', 'Add hills and varied terrain', 'Focus on hiking form']
      },
      {
        week: 4,
        focus: 'Elevation Training',
        activities: ['8-10 mile hikes', 'Daily walking 60-75 minutes', 'Full body strength training'],
        duration: '10-12 hours/week',
        intensity: 'Moderate to Hard',
        notes: ['Seek out steep hills and stairs', 'Increase pack weight to 20-25 lbs', 'Practice uphill technique']
      },
      {
        week: 5,
        focus: 'Back-to-Back Training',
        activities: ['10-12 mile hikes', 'Consecutive day hiking', 'Endurance and strength'],
        duration: '12-15 hours/week',
        intensity: 'Hard',
        notes: ['Hike 2-3 consecutive days', 'Simulate trail conditions', 'Test gear and nutrition']
      },
      {
        week: 6,
        focus: 'Peak Training',
        activities: ['12-15 mile hikes', 'Long day hikes with elevation', 'Full pack training'],
        duration: '15-18 hours/week',
        intensity: 'Hard',
        notes: ['Pack weight 25-30 lbs', 'Include 2000+ ft elevation gain', 'Practice navigation skills']
      },
      {
        week: 7,
        focus: 'Taper and Recovery',
        activities: ['8-10 mile hikes', 'Light strength training', 'Active recovery'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Reduce intensity', 'Focus on recovery', 'Final gear checks']
      },
      {
        week: 8,
        focus: 'Final Preparation',
        activities: ['5-6 mile hikes', 'Light walking', 'Mental preparation'],
        duration: '4-6 hours/week',
        intensity: 'Easy',
        notes: ['Rest and recover', 'Final gear preparation', 'Plan logistics and permits']
      }
    ]
  },
  {
    id: 'grand-canyon',
    name: 'Grand Canyon Rim-to-Rim',
    location: 'Arizona, USA',
    difficulty: 'Extreme',
    duration: '1-2 days',
    distance: '24 miles (39 km)',
    elevation: '2,400 ft to 8,200 ft (730-2,500 m)',
    bestSeason: 'April - May, September - October',
    permits: 'Backcountry permits required for overnight stays',
    preparation: {
      fitness: [
        'Exceptional fitness required for one-day crossing',
        'Practice hiking in extreme heat',
        'Train for steep descents and ascents',
        'Build heat tolerance and endurance',
        'Practice hiking with minimal gear for speed'
      ],
      gear: [
        'Lightweight gear for one-day crossing',
        'Extensive water carrying capacity (6+ liters)',
        'Electrolyte supplements and cooling gear',
        'Quality hiking boots with excellent traction',
        'Sun protection (hat, sunscreen, long sleeves)',
        'Emergency supplies and communication device',
        'Headlamp for early morning start'
      ],
      permits: [
        'Backcountry permits required for overnight stays',
        'Apply for permits 4 months in advance',
        'Limited permits available',
        'Consider day hiking (no permit needed)',
        'Understand permit regulations and restrictions'
      ],
      logistics: [
        'Arrange transportation between rims',
        'Book accommodations at Grand Canyon Village',
        'Plan for extreme heat and weather',
        'Consider shuttle services',
        'Plan for potential trail closures'
      ],
      safety: [
        'Carry extensive water and electrolyte supplements',
        'Be prepared for extreme heat (100°F+)',
        'Know signs of heat exhaustion and heat stroke',
        'Start very early to avoid midday heat',
        'Have emergency communication device',
        'Be prepared for sudden weather changes'
      ],
      training: [
        'Start training 4-6 months in advance',
        'Train in extreme heat conditions',
        'Practice hiking with heavy water loads',
        'Complete multiple long training hikes',
        'Build heat tolerance gradually'
      ]
    },
    highlights: [
      'Iconic Grand Canyon views',
      'Diverse ecosystems from rim to river',
      'Historic Phantom Ranch',
      'Colorado River crossing',
      'Unique geological formations'
    ],
    challenges: [
      'Extreme heat and sun exposure',
      'Steep and sustained elevation changes',
      'Limited water sources',
      'High permit competition',
      'Physically demanding terrain'
    ],
    trainingPlan: [
      {
        week: 1,
        focus: 'Base Building',
        activities: ['3-4 mile hikes', 'Daily walking 30-45 minutes', 'Basic strength training'],
        duration: '4-6 hours/week',
        intensity: 'Easy to Moderate',
        notes: ['Start with flat terrain', 'Focus on consistency', 'Build daily movement habit']
      },
      {
        week: 2,
        focus: 'Endurance Foundation',
        activities: ['5-6 mile hikes', 'Daily walking 45-60 minutes', 'Leg strength exercises'],
        duration: '6-8 hours/week',
        intensity: 'Moderate',
        notes: ['Add slight elevation gain', 'Include squats and lunges', 'Practice with day pack']
      },
      {
        week: 3,
        focus: 'Distance Building',
        activities: ['7-8 mile hikes', 'Daily walking 60 minutes', 'Core and leg strengthening'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Increase pack weight to 15-20 lbs', 'Add hills and varied terrain', 'Focus on hiking form']
      },
      {
        week: 4,
        focus: 'Elevation Training',
        activities: ['8-10 mile hikes', 'Daily walking 60-75 minutes', 'Full body strength training'],
        duration: '10-12 hours/week',
        intensity: 'Moderate to Hard',
        notes: ['Seek out steep hills and stairs', 'Increase pack weight to 20-25 lbs', 'Practice uphill technique']
      },
      {
        week: 5,
        focus: 'Back-to-Back Training',
        activities: ['10-12 mile hikes', 'Consecutive day hiking', 'Endurance and strength'],
        duration: '12-15 hours/week',
        intensity: 'Hard',
        notes: ['Hike 2-3 consecutive days', 'Simulate trail conditions', 'Test gear and nutrition']
      },
      {
        week: 6,
        focus: 'Peak Training',
        activities: ['12-15 mile hikes', 'Long day hikes with elevation', 'Full pack training'],
        duration: '15-18 hours/week',
        intensity: 'Hard',
        notes: ['Pack weight 25-30 lbs', 'Include 2000+ ft elevation gain', 'Practice navigation skills']
      },
      {
        week: 7,
        focus: 'Taper and Recovery',
        activities: ['8-10 mile hikes', 'Light strength training', 'Active recovery'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Reduce intensity', 'Focus on recovery', 'Final gear checks']
      },
      {
        week: 8,
        focus: 'Final Preparation',
        activities: ['5-6 mile hikes', 'Light walking', 'Mental preparation'],
        duration: '4-6 hours/week',
        intensity: 'Easy',
        notes: ['Rest and recover', 'Final gear preparation', 'Plan logistics and permits']
      }
    ]
  },
  {
    id: 'annapurna-base-camp',
    name: 'Annapurna Base Camp Trek',
    location: 'Nepal',
    difficulty: 'Moderate',
    duration: '10-14 days',
    distance: '70 miles (115 km)',
    elevation: '2,700-13,550 ft (820-4,130 m)',
    bestSeason: 'October - November, March - April',
    permits: 'TIMS and ACAP permits required',
    preparation: {
      fitness: [
        'Good cardiovascular fitness required',
        'Practice hiking with 25-35 lb pack',
        'Train for sustained uphill hiking',
        'Build altitude tolerance gradually',
        'Focus on leg strength and endurance'
      ],
      gear: [
        'Layered clothing for temperature changes',
        'Quality hiking boots with good traction',
        'Trekking poles for stability',
        'Sleeping bag rated for cold temperatures',
        'Water purification system',
        'Comprehensive first aid kit',
        'Emergency supplies and communication device'
      ],
      permits: [
        'TIMS (Trekkers Information Management System) permit required',
        'ACAP (Annapurna Conservation Area Permit) required',
        'Obtain permits in Kathmandu or Pokhara',
        'Carry permits at all times',
        'Understand permit regulations'
      ],
      logistics: [
        'Fly to Kathmandu, then to Pokhara',
        'Arrange transportation to trailhead',
        'Book accommodations in Kathmandu and Pokhara',
        'Consider hiring guide and porter',
        'Plan for potential weather delays'
      ],
      safety: [
        'Be prepared for altitude sickness',
        'Carry emergency supplies and communication device',
        'Understand altitude acclimatization',
        'Know how to handle cold weather',
        'Inform someone of your itinerary',
        'Be prepared for potential weather delays'
      ],
      training: [
        'Start training 3-4 months in advance',
        'Practice hiking at moderate altitude',
        'Train with full pack weight',
        'Complete multiple consecutive day hikes',
        'Test gear in cold conditions'
      ]
    },
    highlights: [
      'Stunning Annapurna mountain views',
      'Diverse Nepalese culture and villages',
      'Beautiful rhododendron forests',
      'Hot springs at Jhinu Danda',
      'Well-established tea house system'
    ],
    challenges: [
      'Altitude sickness risk',
      'Unpredictable weather',
      'Steep and sustained ascents',
      'Cold temperatures at higher elevations',
      'Potential for trail closures'
    ],
    trainingPlan: [
      {
        week: 1,
        focus: 'Base Building',
        activities: ['3-4 mile hikes', 'Daily walking 30-45 minutes', 'Basic strength training'],
        duration: '4-6 hours/week',
        intensity: 'Easy to Moderate',
        notes: ['Start with flat terrain', 'Focus on consistency', 'Build daily movement habit']
      },
      {
        week: 2,
        focus: 'Endurance Foundation',
        activities: ['5-6 mile hikes', 'Daily walking 45-60 minutes', 'Leg strength exercises'],
        duration: '6-8 hours/week',
        intensity: 'Moderate',
        notes: ['Add slight elevation gain', 'Include squats and lunges', 'Practice with day pack']
      },
      {
        week: 3,
        focus: 'Distance Building',
        activities: ['7-8 mile hikes', 'Daily walking 60 minutes', 'Core and leg strengthening'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Increase pack weight to 15-20 lbs', 'Add hills and varied terrain', 'Focus on hiking form']
      },
      {
        week: 4,
        focus: 'Elevation Training',
        activities: ['8-10 mile hikes', 'Daily walking 60-75 minutes', 'Full body strength training'],
        duration: '10-12 hours/week',
        intensity: 'Moderate to Hard',
        notes: ['Seek out steep hills and stairs', 'Increase pack weight to 20-25 lbs', 'Practice uphill technique']
      },
      {
        week: 5,
        focus: 'Back-to-Back Training',
        activities: ['10-12 mile hikes', 'Consecutive day hiking', 'Endurance and strength'],
        duration: '12-15 hours/week',
        intensity: 'Hard',
        notes: ['Hike 2-3 consecutive days', 'Simulate trail conditions', 'Test gear and nutrition']
      },
      {
        week: 6,
        focus: 'Peak Training',
        activities: ['12-15 mile hikes', 'Long day hikes with elevation', 'Full pack training'],
        duration: '15-18 hours/week',
        intensity: 'Hard',
        notes: ['Pack weight 25-30 lbs', 'Include 2000+ ft elevation gain', 'Practice navigation skills']
      },
      {
        week: 7,
        focus: 'Taper and Recovery',
        activities: ['8-10 mile hikes', 'Light strength training', 'Active recovery'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Reduce intensity', 'Focus on recovery', 'Final gear checks']
      },
      {
        week: 8,
        focus: 'Final Preparation',
        activities: ['5-6 mile hikes', 'Light walking', 'Mental preparation'],
        duration: '4-6 hours/week',
        intensity: 'Easy',
        notes: ['Rest and recover', 'Final gear preparation', 'Plan logistics and permits']
      }
    ]
  },
  {
    id: 'kilimanjaro',
    name: 'Mount Kilimanjaro',
    location: 'Tanzania',
    difficulty: 'Challenging',
    duration: '6-8 days',
    distance: '35-50 miles (56-80 km) depending on route',
    elevation: '5,900-19,341 ft (1,800-5,895 m)',
    bestSeason: 'January - March, June - October',
    permits: 'Park fees and guide required',
    preparation: {
      fitness: [
        'Excellent cardiovascular fitness required',
        'Practice hiking with 25-35 lb pack',
        'Train for sustained uphill hiking',
        'Build altitude tolerance gradually',
        'Focus on leg strength and endurance'
      ],
      gear: [
        'Layered clothing for extreme temperature changes',
        'Quality hiking boots with excellent traction',
        'Trekking poles for stability',
        'Sleeping bag rated for extreme cold',
        'Water purification system',
        'Comprehensive first aid kit',
        'Emergency supplies and communication device'
      ],
      permits: [
        'Park fees required (varies by route)',
        'Guide and porter services required',
        'Book through licensed tour operator',
        'Obtain permits in advance',
        'Understand permit regulations'
      ],
      logistics: [
        'Fly to Kilimanjaro International Airport',
        'Arrange transportation to trailhead',
        'Book accommodations in Arusha or Moshi',
        'Choose route and tour operator',
        'Plan for potential weather delays'
      ],
      safety: [
        'Be prepared for altitude sickness',
        'Carry emergency supplies and communication device',
        'Understand altitude acclimatization',
        'Know how to handle extreme cold',
        'Inform someone of your itinerary',
        'Be prepared for potential weather delays'
      ],
      training: [
        'Start training 4-6 months in advance',
        'Practice hiking at moderate altitude',
        'Train with full pack weight',
        'Complete multiple consecutive day hikes',
        'Test gear in extreme cold conditions'
      ]
    },
    highlights: [
      'Summit of Africa\'s highest peak',
      'Diverse ecosystems from rainforest to arctic',
      'Stunning views from the roof of Africa',
      'Well-established route system',
      'Opportunity to see diverse wildlife'
    ],
    challenges: [
      'Extreme altitude and altitude sickness risk',
      'Unpredictable weather and extreme cold',
      'Steep and sustained ascents',
      'High cost of guided tours',
      'Potential for summit day weather delays'
    ],
    trainingPlan: [
      {
        week: 1,
        focus: 'Base Building',
        activities: ['3-4 mile hikes', 'Daily walking 30-45 minutes', 'Basic strength training'],
        duration: '4-6 hours/week',
        intensity: 'Easy to Moderate',
        notes: ['Start with flat terrain', 'Focus on consistency', 'Build daily movement habit']
      },
      {
        week: 2,
        focus: 'Endurance Foundation',
        activities: ['5-6 mile hikes', 'Daily walking 45-60 minutes', 'Leg strength exercises'],
        duration: '6-8 hours/week',
        intensity: 'Moderate',
        notes: ['Add slight elevation gain', 'Include squats and lunges', 'Practice with day pack']
      },
      {
        week: 3,
        focus: 'Distance Building',
        activities: ['7-8 mile hikes', 'Daily walking 60 minutes', 'Core and leg strengthening'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Increase pack weight to 15-20 lbs', 'Add hills and varied terrain', 'Focus on hiking form']
      },
      {
        week: 4,
        focus: 'Elevation Training',
        activities: ['8-10 mile hikes', 'Daily walking 60-75 minutes', 'Full body strength training'],
        duration: '10-12 hours/week',
        intensity: 'Moderate to Hard',
        notes: ['Seek out steep hills and stairs', 'Increase pack weight to 20-25 lbs', 'Practice uphill technique']
      },
      {
        week: 5,
        focus: 'Back-to-Back Training',
        activities: ['10-12 mile hikes', 'Consecutive day hiking', 'Endurance and strength'],
        duration: '12-15 hours/week',
        intensity: 'Hard',
        notes: ['Hike 2-3 consecutive days', 'Simulate trail conditions', 'Test gear and nutrition']
      },
      {
        week: 6,
        focus: 'Peak Training',
        activities: ['12-15 mile hikes', 'Long day hikes with elevation', 'Full pack training'],
        duration: '15-18 hours/week',
        intensity: 'Hard',
        notes: ['Pack weight 25-30 lbs', 'Include 2000+ ft elevation gain', 'Practice navigation skills']
      },
      {
        week: 7,
        focus: 'Taper and Recovery',
        activities: ['8-10 mile hikes', 'Light strength training', 'Active recovery'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Reduce intensity', 'Focus on recovery', 'Final gear checks']
      },
      {
        week: 8,
        focus: 'Final Preparation',
        activities: ['5-6 mile hikes', 'Light walking', 'Mental preparation'],
        duration: '4-6 hours/week',
        intensity: 'Easy',
        notes: ['Rest and recover', 'Final gear preparation', 'Plan logistics and permits']
      }
    ]
  },
  {
    id: 'inca-trail',
    name: 'Inca Trail to Machu Picchu',
    location: 'Peru',
    difficulty: 'Moderate',
    duration: '4 days',
    distance: '26 miles (42 km)',
    elevation: '8,200-13,800 ft (2,500-4,200 m)',
    bestSeason: 'May - September (dry season)',
    permits: 'Permits required and limited',
    preparation: {
      fitness: [
        'Good cardiovascular fitness required',
        'Practice hiking with 25-35 lb pack',
        'Train for steep ascents and descents',
        'Build altitude tolerance gradually',
        'Focus on leg strength and endurance'
      ],
      gear: [
        'Layered clothing for temperature changes',
        'Quality hiking boots with good traction',
        'Trekking poles for stability',
        'Sleeping bag rated for cold temperatures',
        'Water purification system',
        'Comprehensive first aid kit',
        'Emergency supplies and communication device'
      ],
      permits: [
        'Permits required and limited (500 per day)',
        'Book through licensed tour operator',
        'Apply for permits 6+ months in advance',
        'Understand permit regulations',
        'Carry permits at all times'
      ],
      logistics: [
        'Fly to Lima, then to Cusco',
        'Arrange transportation to trailhead',
        'Book accommodations in Cusco',
        'Choose tour operator and route',
        'Plan for altitude acclimatization'
      ],
      safety: [
        'Be prepared for altitude sickness',
        'Carry emergency supplies and communication device',
        'Understand altitude acclimatization',
        'Know how to handle cold weather',
        'Inform someone of your itinerary',
        'Be prepared for potential weather delays'
      ],
      training: [
        'Start training 3-4 months in advance',
        'Practice hiking at moderate altitude',
        'Train with full pack weight',
        'Complete multiple consecutive day hikes',
        'Test gear in cold conditions'
      ]
    },
    highlights: [
      'Historic Inca ruins and archaeological sites',
      'Stunning Andean mountain views',
      'Diverse ecosystems and wildlife',
      'Well-maintained trail system',
      'Arrival at Machu Picchu at sunrise'
    ],
    challenges: [
      'Altitude sickness risk',
      'Unpredictable weather',
      'Steep and sustained ascents',
      'High permit competition',
      'Cold temperatures at higher elevations'
    ],
    trainingPlan: [
      {
        week: 1,
        focus: 'Base Building',
        activities: ['3-4 mile hikes', 'Daily walking 30-45 minutes', 'Basic strength training'],
        duration: '4-6 hours/week',
        intensity: 'Easy to Moderate',
        notes: ['Start with flat terrain', 'Focus on consistency', 'Build daily movement habit']
      },
      {
        week: 2,
        focus: 'Endurance Foundation',
        activities: ['5-6 mile hikes', 'Daily walking 45-60 minutes', 'Leg strength exercises'],
        duration: '6-8 hours/week',
        intensity: 'Moderate',
        notes: ['Add slight elevation gain', 'Include squats and lunges', 'Practice with day pack']
      },
      {
        week: 3,
        focus: 'Distance Building',
        activities: ['7-8 mile hikes', 'Daily walking 60 minutes', 'Core and leg strengthening'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Increase pack weight to 15-20 lbs', 'Add hills and varied terrain', 'Focus on hiking form']
      },
      {
        week: 4,
        focus: 'Elevation Training',
        activities: ['8-10 mile hikes', 'Daily walking 60-75 minutes', 'Full body strength training'],
        duration: '10-12 hours/week',
        intensity: 'Moderate to Hard',
        notes: ['Seek out steep hills and stairs', 'Increase pack weight to 20-25 lbs', 'Practice uphill technique']
      },
      {
        week: 5,
        focus: 'Back-to-Back Training',
        activities: ['10-12 mile hikes', 'Consecutive day hiking', 'Endurance and strength'],
        duration: '12-15 hours/week',
        intensity: 'Hard',
        notes: ['Hike 2-3 consecutive days', 'Simulate trail conditions', 'Test gear and nutrition']
      },
      {
        week: 6,
        focus: 'Peak Training',
        activities: ['12-15 mile hikes', 'Long day hikes with elevation', 'Full pack training'],
        duration: '15-18 hours/week',
        intensity: 'Hard',
        notes: ['Pack weight 25-30 lbs', 'Include 2000+ ft elevation gain', 'Practice navigation skills']
      },
      {
        week: 7,
        focus: 'Taper and Recovery',
        activities: ['8-10 mile hikes', 'Light strength training', 'Active recovery'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Reduce intensity', 'Focus on recovery', 'Final gear checks']
      },
      {
        week: 8,
        focus: 'Final Preparation',
        activities: ['5-6 mile hikes', 'Light walking', 'Mental preparation'],
        duration: '4-6 hours/week',
        intensity: 'Easy',
        notes: ['Rest and recover', 'Final gear preparation', 'Plan logistics and permits']
      }
    ]
  },
  {
    id: 'everest-base-camp',
    name: 'Everest Base Camp Trek',
    location: 'Nepal',
    difficulty: 'Challenging',
    duration: '12-16 days',
    distance: '80 miles (130 km)',
    elevation: '9,200-17,600 ft (2,800-5,365 m)',
    bestSeason: 'October - November, March - April',
    permits: 'TIMS and Sagarmatha National Park permits required',
    preparation: {
      fitness: [
        'Excellent cardiovascular fitness required',
        'Practice hiking with 25-35 lb pack',
        'Train for sustained uphill hiking',
        'Build altitude tolerance gradually',
        'Focus on leg strength and endurance'
      ],
      gear: [
        'Layered clothing for extreme temperature changes',
        'Quality hiking boots with excellent traction',
        'Trekking poles for stability',
        'Sleeping bag rated for extreme cold',
        'Water purification system',
        'Comprehensive first aid kit',
        'Emergency supplies and communication device'
      ],
      permits: [
        'TIMS (Trekkers Information Management System) permit required',
        'Sagarmatha National Park permit required',
        'Obtain permits in Kathmandu',
        'Carry permits at all times',
        'Understand permit regulations'
      ],
      logistics: [
        'Fly to Kathmandu, then to Lukla',
        'Arrange transportation to trailhead',
        'Book accommodations in Kathmandu',
        'Consider hiring guide and porter',
        'Plan for potential weather delays'
      ],
      safety: [
        'Be prepared for altitude sickness',
        'Carry emergency supplies and communication device',
        'Understand altitude acclimatization',
        'Know how to handle extreme cold',
        'Inform someone of your itinerary',
        'Be prepared for potential weather delays'
      ],
      training: [
        'Start training 4-6 months in advance',
        'Practice hiking at moderate altitude',
        'Train with full pack weight',
        'Complete multiple consecutive day hikes',
        'Test gear in extreme cold conditions'
      ]
    },
    highlights: [
      'Views of Mount Everest and surrounding peaks',
      'Diverse Sherpa culture and villages',
      'Beautiful rhododendron forests',
      'Well-established tea house system',
      'Opportunity to see diverse wildlife'
    ],
    challenges: [
      'Extreme altitude and altitude sickness risk',
      'Unpredictable weather and extreme cold',
      'Steep and sustained ascents',
      'Cold temperatures at higher elevations',
      'Potential for trail closures'
    ],
    trainingPlan: [
      {
        week: 1,
        focus: 'Base Building',
        activities: ['3-4 mile hikes', 'Daily walking 30-45 minutes', 'Basic strength training'],
        duration: '4-6 hours/week',
        intensity: 'Easy to Moderate',
        notes: ['Start with flat terrain', 'Focus on consistency', 'Build daily movement habit']
      },
      {
        week: 2,
        focus: 'Endurance Foundation',
        activities: ['5-6 mile hikes', 'Daily walking 45-60 minutes', 'Leg strength exercises'],
        duration: '6-8 hours/week',
        intensity: 'Moderate',
        notes: ['Add slight elevation gain', 'Include squats and lunges', 'Practice with day pack']
      },
      {
        week: 3,
        focus: 'Distance Building',
        activities: ['7-8 mile hikes', 'Daily walking 60 minutes', 'Core and leg strengthening'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Increase pack weight to 15-20 lbs', 'Add hills and varied terrain', 'Focus on hiking form']
      },
      {
        week: 4,
        focus: 'Elevation Training',
        activities: ['8-10 mile hikes', 'Daily walking 60-75 minutes', 'Full body strength training'],
        duration: '10-12 hours/week',
        intensity: 'Moderate to Hard',
        notes: ['Seek out steep hills and stairs', 'Increase pack weight to 20-25 lbs', 'Practice uphill technique']
      },
      {
        week: 5,
        focus: 'Back-to-Back Training',
        activities: ['10-12 mile hikes', 'Consecutive day hiking', 'Endurance and strength'],
        duration: '12-15 hours/week',
        intensity: 'Hard',
        notes: ['Hike 2-3 consecutive days', 'Simulate trail conditions', 'Test gear and nutrition']
      },
      {
        week: 6,
        focus: 'Peak Training',
        activities: ['12-15 mile hikes', 'Long day hikes with elevation', 'Full pack training'],
        duration: '15-18 hours/week',
        intensity: 'Hard',
        notes: ['Pack weight 25-30 lbs', 'Include 2000+ ft elevation gain', 'Practice navigation skills']
      },
      {
        week: 7,
        focus: 'Taper and Recovery',
        activities: ['8-10 mile hikes', 'Light strength training', 'Active recovery'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Reduce intensity', 'Focus on recovery', 'Final gear checks']
      },
      {
        week: 8,
        focus: 'Final Preparation',
        activities: ['5-6 mile hikes', 'Light walking', 'Mental preparation'],
        duration: '4-6 hours/week',
        intensity: 'Easy',
        notes: ['Rest and recover', 'Final gear preparation', 'Plan logistics and permits']
      }
    ]
  },
  {
    id: 'tour-du-mont-blanc',
    name: 'Tour du Mont Blanc',
    location: 'France, Italy, Switzerland',
    difficulty: 'Moderate',
    duration: '10-12 days',
    distance: '105 miles (170 km)',
    elevation: '3,300-8,700 ft (1,000-2,650 m)',
    bestSeason: 'June - September',
    permits: 'No permits required',
    preparation: {
      fitness: [
        'Good cardiovascular fitness required',
        'Practice hiking with 25-35 lb pack',
        'Train for sustained uphill hiking',
        'Build endurance for consecutive hiking days',
        'Focus on leg strength and stability'
      ],
      gear: [
        'Layered clothing for variable weather',
        'Quality hiking boots with good traction',
        'Trekking poles for stability',
        'Sleeping bag rated for cold temperatures',
        'Water purification system',
        'Comprehensive first aid kit',
        'Emergency supplies and communication device'
      ],
      permits: [
        'No permits required for the trail',
        'Check individual accommodation requirements',
        'Some areas may require reservations',
        'Respect private property boundaries',
        'Understand local regulations'
      ],
      logistics: [
        'Fly to Geneva, then to Chamonix',
        'Arrange transportation to trailhead',
        'Book accommodations in advance',
        'Plan for weather changes',
        'Consider guided vs. self-guided options'
      ],
      safety: [
        'Be prepared for weather changes',
        'Carry emergency supplies and communication device',
        'Understand mountain safety',
        'Know how to handle cold weather',
        'Inform someone of your itinerary',
        'Be prepared for potential trail closures'
      ],
      training: [
        'Start training 3-4 months in advance',
        'Practice hiking in variable weather',
        'Train with full pack weight',
        'Complete multiple consecutive day hikes',
        'Test gear in cold conditions'
      ]
    },
    highlights: [
      'Stunning views of Mont Blanc massif',
      'Diverse European cultures and cuisines',
      'Beautiful alpine lakes and peaks',
      'Well-maintained trail system',
      'Opportunity to see diverse wildlife'
    ],
    challenges: [
      'Unpredictable weather',
      'Steep and sustained ascents',
      'High cost of accommodations',
      'Popular trail can be crowded',
      'Potential for trail closures'
    ],
    trainingPlan: [
      {
        week: 1,
        focus: 'Base Building',
        activities: ['3-4 mile hikes', 'Daily walking 30-45 minutes', 'Basic strength training'],
        duration: '4-6 hours/week',
        intensity: 'Easy to Moderate',
        notes: ['Start with flat terrain', 'Focus on consistency', 'Build daily movement habit']
      },
      {
        week: 2,
        focus: 'Endurance Foundation',
        activities: ['5-6 mile hikes', 'Daily walking 45-60 minutes', 'Leg strength exercises'],
        duration: '6-8 hours/week',
        intensity: 'Moderate',
        notes: ['Add slight elevation gain', 'Include squats and lunges', 'Practice with day pack']
      },
      {
        week: 3,
        focus: 'Distance Building',
        activities: ['7-8 mile hikes', 'Daily walking 60 minutes', 'Core and leg strengthening'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Increase pack weight to 15-20 lbs', 'Add hills and varied terrain', 'Focus on hiking form']
      },
      {
        week: 4,
        focus: 'Elevation Training',
        activities: ['8-10 mile hikes', 'Daily walking 60-75 minutes', 'Full body strength training'],
        duration: '10-12 hours/week',
        intensity: 'Moderate to Hard',
        notes: ['Seek out steep hills and stairs', 'Increase pack weight to 20-25 lbs', 'Practice uphill technique']
      },
      {
        week: 5,
        focus: 'Back-to-Back Training',
        activities: ['10-12 mile hikes', 'Consecutive day hiking', 'Endurance and strength'],
        duration: '12-15 hours/week',
        intensity: 'Hard',
        notes: ['Hike 2-3 consecutive days', 'Simulate trail conditions', 'Test gear and nutrition']
      },
      {
        week: 6,
        focus: 'Peak Training',
        activities: ['12-15 mile hikes', 'Long day hikes with elevation', 'Full pack training'],
        duration: '15-18 hours/week',
        intensity: 'Hard',
        notes: ['Pack weight 25-30 lbs', 'Include 2000+ ft elevation gain', 'Practice navigation skills']
      },
      {
        week: 7,
        focus: 'Taper and Recovery',
        activities: ['8-10 mile hikes', 'Light strength training', 'Active recovery'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Reduce intensity', 'Focus on recovery', 'Final gear checks']
      },
      {
        week: 8,
        focus: 'Final Preparation',
        activities: ['5-6 mile hikes', 'Light walking', 'Mental preparation'],
        duration: '4-6 hours/week',
        intensity: 'Easy',
        notes: ['Rest and recover', 'Final gear preparation', 'Plan logistics and permits']
      }
    ]
  },
  {
    id: 'dolomites',
    name: 'Dolomites Alta Via',
    location: 'Italy',
    difficulty: 'Challenging',
    duration: '8-12 days',
    distance: '75 miles (120 km)',
    elevation: '4,600-9,800 ft (1,400-3,000 m)',
    bestSeason: 'July - September',
    permits: 'No permits required',
    preparation: {
      fitness: [
        'Excellent cardiovascular fitness required',
        'Practice hiking with 25-35 lb pack',
        'Train for sustained uphill hiking',
        'Build endurance for consecutive hiking days',
        'Focus on leg strength and stability'
      ],
      gear: [
        'Layered clothing for variable weather',
        'Quality hiking boots with excellent traction',
        'Trekking poles for stability',
        'Sleeping bag rated for cold temperatures',
        'Water purification system',
        'Comprehensive first aid kit',
        'Emergency supplies and communication device'
      ],
      permits: [
        'No permits required for the trail',
        'Check individual accommodation requirements',
        'Some areas may require reservations',
        'Respect private property boundaries',
        'Understand local regulations'
      ],
      logistics: [
        'Fly to Venice or Milan, then to Cortina',
        'Arrange transportation to trailhead',
        'Book accommodations in advance',
        'Plan for weather changes',
        'Consider guided vs. self-guided options'
      ],
      safety: [
        'Be prepared for weather changes',
        'Carry emergency supplies and communication device',
        'Understand mountain safety',
        'Know how to handle cold weather',
        'Inform someone of your itinerary',
        'Be prepared for potential trail closures'
      ],
      training: [
        'Start training 4-6 months in advance',
        'Practice hiking in variable weather',
        'Train with full pack weight',
        'Complete multiple consecutive day hikes',
        'Test gear in cold conditions'
      ]
    },
    highlights: [
      'Stunning Dolomite mountain views',
      'Diverse Italian culture and cuisine',
      'Beautiful alpine lakes and peaks',
      'Well-maintained trail system',
      'Opportunity to see diverse wildlife'
    ],
    challenges: [
      'Unpredictable weather',
      'Steep and sustained ascents',
      'High cost of accommodations',
      'Popular trail can be crowded',
      'Potential for trail closures'
    ],
    trainingPlan: [
      {
        week: 1,
        focus: 'Base Building',
        activities: ['3-4 mile hikes', 'Daily walking 30-45 minutes', 'Basic strength training'],
        duration: '4-6 hours/week',
        intensity: 'Easy to Moderate',
        notes: ['Start with flat terrain', 'Focus on consistency', 'Build daily movement habit']
      },
      {
        week: 2,
        focus: 'Endurance Foundation',
        activities: ['5-6 mile hikes', 'Daily walking 45-60 minutes', 'Leg strength exercises'],
        duration: '6-8 hours/week',
        intensity: 'Moderate',
        notes: ['Add slight elevation gain', 'Include squats and lunges', 'Practice with day pack']
      },
      {
        week: 3,
        focus: 'Distance Building',
        activities: ['7-8 mile hikes', 'Daily walking 60 minutes', 'Core and leg strengthening'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Increase pack weight to 15-20 lbs', 'Add hills and varied terrain', 'Focus on hiking form']
      },
      {
        week: 4,
        focus: 'Elevation Training',
        activities: ['8-10 mile hikes', 'Daily walking 60-75 minutes', 'Full body strength training'],
        duration: '10-12 hours/week',
        intensity: 'Moderate to Hard',
        notes: ['Seek out steep hills and stairs', 'Increase pack weight to 20-25 lbs', 'Practice uphill technique']
      },
      {
        week: 5,
        focus: 'Back-to-Back Training',
        activities: ['10-12 mile hikes', 'Consecutive day hiking', 'Endurance and strength'],
        duration: '12-15 hours/week',
        intensity: 'Hard',
        notes: ['Hike 2-3 consecutive days', 'Simulate trail conditions', 'Test gear and nutrition']
      },
      {
        week: 6,
        focus: 'Peak Training',
        activities: ['12-15 mile hikes', 'Long day hikes with elevation', 'Full pack training'],
        duration: '15-18 hours/week',
        intensity: 'Hard',
        notes: ['Pack weight 25-30 lbs', 'Include 2000+ ft elevation gain', 'Practice navigation skills']
      },
      {
        week: 7,
        focus: 'Taper and Recovery',
        activities: ['8-10 mile hikes', 'Light strength training', 'Active recovery'],
        duration: '8-10 hours/week',
        intensity: 'Moderate',
        notes: ['Reduce intensity', 'Focus on recovery', 'Final gear checks']
      },
      {
        week: 8,
        focus: 'Final Preparation',
        activities: ['5-6 mile hikes', 'Light walking', 'Mental preparation'],
        duration: '4-6 hours/week',
        intensity: 'Easy',
        notes: ['Rest and recover', 'Final gear preparation', 'Plan logistics and permits']
      }
    ]
  }
];

const TrailPrepScreen: React.FC = () => {
  const { currentSport, theme } = useTheme();
  const [selectedHike, setSelectedHike] = useState<FamousHike | null>(null);
  const [activeTab, setActiveTab] = useState<'preparation' | 'training'>('preparation');

  const handleHikeSelect = (hikeId: string) => {
    const hike = famousHikes.find(h => h.id === hikeId);
    setSelectedHike(hike || null);
  };

  if (currentSport.id !== 'hiking') {
    return (
      <div className="trail-prep-screen">
        <div className="container">
          <h1>Trail Prep</h1>
          <p>This feature is only available for hiking.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="trail-prep-screen">
      <div className="container">
        <h1>Trail Prep - Famous Hikes</h1>
        <p className="trail-prep-intro">
          Prepare for the world's most famous hiking trails with detailed preparation guides, 
          gear recommendations, and safety tips for each destination.
        </p>

        <div className="hike-selector">
          <label htmlFor="hike-select">Select a Famous Hike:</label>
          <select 
            id="hike-select"
            value={selectedHike?.id || ''}
            onChange={(e) => handleHikeSelect(e.target.value)}
            className="hike-dropdown"
          >
            <option value="">Choose a hike...</option>
            {famousHikes.map((hike) => (
              <option key={hike.id} value={hike.id}>
                {hike.name} - {hike.location}
              </option>
            ))}
          </select>
        </div>

        {selectedHike && (
          <div className="hike-details">
            <div className="hike-header">
              <h2>{selectedHike.name}</h2>
              <p className="hike-location">{selectedHike.location}</p>
              <div className="hike-stats">
                <div className="stat">
                  <span className="stat-label">Difficulty:</span>
                  <span className={`stat-value difficulty-${selectedHike.difficulty.toLowerCase()}`}>
                    {selectedHike.difficulty}
                  </span>
                </div>
                <div className="stat">
                  <span className="stat-label">Duration:</span>
                  <span className="stat-value">{selectedHike.duration}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Distance:</span>
                  <span className="stat-value">{selectedHike.distance}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Elevation:</span>
                  <span className="stat-value">{selectedHike.elevation}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Best Season:</span>
                  <span className="stat-value">{selectedHike.bestSeason}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Permits:</span>
                  <span className="stat-value">{selectedHike.permits}</span>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="tab-navigation">
              <button
                className={`tab-button ${activeTab === 'preparation' ? 'active' : ''}`}
                onClick={() => setActiveTab('preparation')}
              >
                📋 Preparation Guide
              </button>
              <button
                className={`tab-button ${activeTab === 'training' ? 'active' : ''}`}
                onClick={() => setActiveTab('training')}
              >
                🏃‍♂️ 8-Week Training Plan
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'preparation' && (
              <div className="hike-content">
                <div className="preparation-sections">
                <div className="prep-section">
                  <h3>Fitness Preparation</h3>
                  <ul>
                    {selectedHike.preparation.fitness.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="prep-section">
                  <h3>Essential Gear</h3>
                  <ul>
                    {selectedHike.preparation.gear.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="prep-section">
                  <h3>Permits & Regulations</h3>
                  <ul>
                    {selectedHike.preparation.permits.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="prep-section">
                  <h3>Logistics & Planning</h3>
                  <ul>
                    {selectedHike.preparation.logistics.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="prep-section">
                  <h3>Safety Considerations</h3>
                  <ul>
                    {selectedHike.preparation.safety.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>

                <div className="prep-section">
                  <h3>Training Timeline</h3>
                  <ul>
                    {selectedHike.preparation.training.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="hike-highlights">
                <div className="highlights-section">
                  <h3>Trail Highlights</h3>
                  <ul>
                    {selectedHike.highlights.map((highlight, index) => (
                      <li key={index}>{highlight}</li>
                    ))}
                  </ul>
                </div>

                <div className="challenges-section">
                  <h3>Key Challenges</h3>
                  <ul>
                    {selectedHike.challenges.map((challenge, index) => (
                      <li key={index}>{challenge}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            )}

            {activeTab === 'training' && (
              <div className="training-plan-content">
                <div className="training-intro">
                  <h3>8-Week Training Plan</h3>
                  <p>This progressive training plan will prepare you for the physical demands of {selectedHike.name}. 
                     Start 8 weeks before your planned hike date and adjust based on your current fitness level.</p>
                </div>
                
                <div className="training-weeks">
                  {selectedHike.trainingPlan.map((week) => (
                    <div key={week.week} className="training-week">
                      <div className="week-header">
                        <h4>Week {week.week}</h4>
                        <span className="week-focus">{week.focus}</span>
                      </div>
                      <div className="week-details">
                        <div className="week-meta">
                          <div className="meta-item">
                            <strong>Duration:</strong> {week.duration}
                          </div>
                          <div className="meta-item">
                            <strong>Intensity:</strong> {week.intensity}
                          </div>
                        </div>
                        <div className="week-activities">
                          <h5>Activities:</h5>
                          <ul>
                            {week.activities.map((activity, index) => (
                              <li key={index}>{activity}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="week-notes">
                          <h5>Notes:</h5>
                          <ul>
                            {week.notes.map((note, index) => (
                              <li key={index}>{note}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrailPrepScreen;
